// CounterCurrentColumnSimulator.js (converging, synchronous)
// PH-flash column with PI control on condenser/reboiler and Murphree tray efficiencies.
// Side-draws are removed INSIDE the iteration so internals reflect mass loss.
// After convergence, products (D + Σ side draws + B) are scaled so Σ products = feedRate_kgph,
// while INTERNAL traffic is shown on a feed-anchored basis for realistic magnitudes.

import { flashPH, flashPH_saturated } from "../thermo/PHFlash";
import { hVap, hLiq } from "../thermo/Enthalpy";
import { getEOSForTray } from "../thermo/EOSK";

export function simulateColumn({
  crudeKey = null,
  components,
  feedZ,
  feedTray,
  Tfeed,
  Ttop,
  Tbottom,
  eosMode = "auto",
  eosManual = null,
  Ptop,
  Pdrop,
  trays = 32,

  refluxRatio = 2.0,
  // In a real ADU/CDU there isn't a simple "reboiler"; stripping steam + furnace
  // provide vaporization. Keep the default Vb/B small to avoid unphysical 100% boilup.
  reboilRatio = 0.06,
  // Reboiler duty input (kW). How it's used depends on reboilerSpec.
  // Keep defaults moderate for a 100,000 kg/h demo case.
  Qr_kW: Qr_kW_in = 6000,

  // --- Boundary specification modes (UI-facing) ---
  // Condenser:
  //   - 'temperature': Tc = Ttop (input), Qc is calculated
  //   - 'duty':        Qc = Qc_kW_in (input), Tc floats
  condenserSpec = "temperature",
  Qc_kW: Qc_kW_in = -6000,

  // Reboiler:
  //   - 'duty':        Qr = Qr_kW_in (input), Treb floats
  //   - 'temperature': Treb = Tbottom (input), Qr floats
  //   - 'boilup':      Vb/B = reboilRatio (input), Qr floats
  reboilerSpec = "duty",
  condenserType = "total",
  reboilerType = "partial",

  // liquid side-draw fraction on tray i (0-based). Values ~0..0.5 recommended.
  sideDrawSpecs = {},

  // Optional label map for UI: { [1-based tray]: "Label" }
  drawLabels = null,

  // User mass-flow basis
  feedRate_kgph = 100000,

  // numerics
  maxIter = 80,
  relax = 0.45,
  relaxT = 0.3,
  topApproach = 10,
  bottomApproach = 10,

  // PI gains (kW/K)
  Kc_Q = 600,
  Kr_Q = 600,
  Ki_Q = 0.02,

  murphree = {
    etaV_top: 0.75,
    etaV_mid: 0.65,
    etaV_bot: 0.55,
    etaL_top: 1.0,
    etaL_mid: 1.0,
    etaL_bot: 1.0,
  },

  onProgress,
  debug = {},
}) {
  // EOS selection (kept local so we never depend on an undefined helper).
  // trayIndex0 is 0-based: 0=bottom tray, (trays-1)=top.
  const pickEOS = (trayIndex0) =>
    getEOSForTray(trayIndex0, trays, crudeKey, eosMode, eosManual);
  const N = trays;
  const f = feedTray - 1;

  // Internal mass-flow scaling: internals are on a feed=1 basis (dimensionless).
  // Convert to kg/s so kW duties can be applied consistently as kJ/kg.
  const feed_kgps = Math.max(1e-12, feedRate_kgph / 3600);

  // OPTION A: Bottom temperature is controlled by Qr.
  // Do NOT enforce boil-up ratio at the same time (otherwise over-constrains the system).
  const enforceBoilupRatio = false;

  // Diagnostics (emit at iterPrint cadence to avoid flooding the run log)
  // Captures what the bottom tray ("reboiler-equivalent") PH flash actually computed.
  let lastReboilerDiag = null;

  // ---- Solver diagnostics (UI-facing; NOT sent to Run log) ----
  // These are returned for the UI to display a single diagnostic line above
  // the existing "Temperature spikes" message. This mirrors how HYSYS lets
  // users see why a spec may be hard to satisfy (e.g., duty limits / clamping).
  const diagnostics = [];
  const addDiag = (level, code, message, meta = null) => {
    diagnostics.push({
      level: String(level || "info"),
      code: String(code || ""),
      message: String(message || ""),
      ...(meta ? { meta } : {}),
    });
  };

  // Track common spec-limit events (used to generate diagnostics near the end).
  let rb_hitDutyMax = false;
  let rb_hitDutyMin = false;
  let rb_vfracClamped = false;
  let rb_vfracRaw = null;
  let rb_vfracUsed = null;
  let c_tcAtMin = false;
  let c_tcAtMax = false;

  // Wide temperature safety bounds for iteration (avoid pinning to Ttop).
  // Use these for internal iteration clamping; final displays can reference setpoints.
  const T_MIN = 250; // K
  const T_MAX = 900; // K

  // Condenser outlet temperature (may float if condenserSpec === 'duty')
  let Tc = clamp(Ttop, T_MIN, T_MAX);

  // Active setpoints (for reporting / UI)
  const Tc_set_K = clamp(Ttop, T_MIN, T_MAX);
  const Qc_set_kW = clamp(Qc_kW_in, -80000, 0);
  const Treb_set_K = clamp(Tbottom, T_MIN, T_MAX);

  const tick = (p) => {
    try {
      onProgress && onProgress(p);
    } catch {}
  };
  const iterPrint = Number.isFinite(debug.iterPrint) ? debug.iterPrint : 5;
  const trayPrint = Number.isFinite(debug.trayPrint) ? debug.trayPrint : 1;

  // Initial T and P
  const T = initTrayTempsTwoSegment({ N, Ttop, Tbottom, Tfeed, f });
  const P = initTrayPressures({ N, Ptop, Pdrop });

  tick({ stage: "init", trays: N, feedTray, Ttop, Tbottom });

  // Crude-specific safeguards (default-off so Brent/WCS behavior is unchanged).
  // WTI has exhibited over-aggressive single-phase short-circuiting in PH flashes
  // when pseudo-component property sets are marginal.
  // NOTE:
  // We found WTI frequently getting forced into the PHFlash "singlePhase:V" shortcut
  // (many trays stuck at ~feed-T with V=1.0). This shortcut is useful for stability
  // when K-values are obviously all >1 or all <1, but for WTI it was triggering too
  // early and preventing the column from developing a realistic two-phase profile.
  //
  // So: disable the shortcut for WTI specifically.
  const disableSinglePhaseShortCircuit =
    /(^|\b)(wti|west\s+texas\s+intermediate)(\b|$)/i.test(
      String(crudeKey ?? "")
    );
  const baseFlashOpts = {
    eosMode,
    eosManual,
    // Keep existing logging behavior.
    log: (s) => console.log(s),
    // Only changes behavior for WTI.
    disableSinglePhaseShortCircuit,
  };

  // Feed PH flash at tray f
  const Hfeed =
    0.5 * hVap(feedZ, T[f], components, P[f]) +
    0.5 * hLiq(feedZ, T[f], components, P[f]);
  const {
    T: TfeedSolved,
    V: Vf,
    x: xf,
    y: yf,
  } = flashPH({
    P: P[f],
    z: feedZ,
    Htarget: Hfeed,
    components,
    trayIndex: f,
    trays: N,
    Tseed: T[f],
    ...baseFlashOpts,
  });
  T[f] = TfeedSolved;

  // Internal streams (dimensionless)
  let V_up = Array.from({ length: N }, () => 0.2);
  let Y_up = Array.from({ length: N }, () => feedZ.slice());
  let L_dn = Array.from({ length: N }, () => 0.8);
  let X_dn = Array.from({ length: N }, () => feedZ.slice());

  // Boundaries
  let L_ref = 0.5,
    x_ref = feedZ.slice();
  let V_boil = 0.3,
    y_boil = feedZ.slice();

  // Duties
  let Qc_kW = 0;
  // Reboiler duty may be controlled (temperature/boilup modes) or fixed (duty mode)
  let Qr_kW = clamp(Qr_kW_in, 0, 80000);

  // PI integrators (used only when a boundary spec requires control)
  let ei_top_int = 0; // condenser
  let ei_bot_int = 0; // reboiler
  let last_Qc_kW = Qc_kW;
  let signFlips_Qc = 0;

  let B_dim_last = 0.7;
  let xB_last = feedZ.slice();
  let Treb_last = clamp(Tbottom, T_MIN, T_MAX);
  let Vfrac_reb_last = 0.0;

  // Side-draws (dimensionless, on feed=1 basis) for the *current iteration*.
  // This MUST be reset each iteration (do not accumulate across iterations).
  let sideDraw_dim = new Array(N).fill(0);

  // --------- Iterate ----------
  for (let iter = 0; iter < maxIter; iter++) {
    const Ttop_prev = T[N - 1];
    const Tbot_prev = T[0];
    // Reset per-iteration side draws
    sideDraw_dim = new Array(N).fill(0);

    if (iter % iterPrint === 0) {
      tick({ stage: "iter", iter, Ttop: T[N - 1], Tbot: T[0], Qc_kW, Qr_kW });

      // A) Confirm whether UI parameters are reaching the solver
      tick({
        stage: "diagInputs",
        iter,
        crude: crudeKey,
        Qr_kW,
        reboilRatio,
        eosMode,
        eosManual,
      });
    }

    const next_L_dn = new Array(N).fill(0);
    const next_X_dn = new Array(N).fill(null);

    // Upward sweep
    let V_in = V_boil;
    let y_in = y_boil;
    // Vapor enthalpy carry-over: use the vapor stream enthalpy coming from the tray below
    // instead of re-evaluating at the local mixed temperature each tray (reduces axial oscillations).
    let Hvap_carry = hVap(y_in, clamp(T[0], T_MIN, T_MAX), components, P[0]);

    for (let i = 0; i < N; i++) {
      // Treat Tray 1 (index 0) as the REBOILER boundary drum (not an equilibrium stage).
      // The first equilibrium stage is Tray 2 (index 1).
      if (i === 0) {
        T[i] = clamp(Treb_last, T_MIN, T_MAX);
        V_up[i] = (1 - relax) * V_up[i] + relax * V_boil;
        Y_up[i] = blendVec(Y_up[i], y_boil, relax);
        next_L_dn[i] = 0;
        next_X_dn[i] = feedZ.slice();
        continue;
      }
      // Treat Tray N (index N-1) as the CONDENSER boundary drum (not an equilibrium stage).
      // The top equilibrium stage is Tray N-1 (index N-2).
      if (i === N - 1) {
        T[i] = Tc;
        V_up[i] = V_up[i - 1];
        Y_up[i] = Y_up[i - 1];
        next_L_dn[i] = 0;
        next_X_dn[i] = x_ref.slice();
        continue;
      }
      if (trayPrint > 0 && i % trayPrint === 0)
        tick({ stage: "trayStart", iter, tray: i, trays: N });

      const L_in = i === N - 1 ? L_ref : L_dn[i + 1] ?? L_ref;
      const x_in = i === N - 1 ? x_ref : X_dn[i + 1] ?? x_ref;

      const addV = i === f ? Vf : 0;
      const addL = i === f ? 1 - Vf : 0;

      const M = Math.max(1e-12, V_in + L_in + addV + addL);
      const z = new Array(feedZ.length).fill(0);
      for (let k = 0; k < z.length; k++) {
        const num =
          V_in * y_in[k] + L_in * x_in[k] + addV * yf[k] + addL * xf[k];
        z[k] = Math.max(0, num / M);
      }
      normalize(z);

      const Tvin = i > 0 ? T[i - 1] : T[i];
      const Tlin = i < N - 1 ? T[i + 1] : T[i];
      const T_liq_in = i === N - 1 ? Tc : Tlin;

      const Hmix =
        (V_in * Hvap_carry +
          L_in * hLiq(x_in, T_liq_in, components, P[i]) +
          addV * hVap(yf, TfeedSolved, components, P[i]) +
          addL * hLiq(xf, TfeedSolved, components, P[i])) /
        Math.max(1e-12, M);

      const Htarget = Hmix;

      // Tray efficiency (Murphree) by section.
      // We apply it inside EOSK (via an effective K-value) so that RR + PH flash see a consistent, softened equilibrium.
      const etaV = etaBySection(
        i,
        N,
        f,
        murphree.etaV_top,
        murphree.etaV_mid,
        murphree.etaV_bot
      );

      const ans = flashPH({
        P: P[i],
        z,
        Htarget,
        components,
        trayIndex: i,
        trays: N,
        Tseed: T[i],
        murphreeEtaV: etaV,
        ...baseFlashOpts,
      });
      const {
        T: Tnew,
        V: Vfrac_eq,
        x: x_eq,
        y: y_eq,
        status: phStatus,
        singlePhase: phSinglePhase,
        dH: ph_dH,
        Hcalc: ph_Hcalc,
      } = ans;

      // B) Log what the reboiler-equivalent (bottom tray) PH flash actually computes
      // IMPORTANT: use the actual liquid downflow into the reboiler (L_in) via Hmix, NOT the bottoms draw.
      if (i === 0 && iter % iterPrint === 0) {
        const V_out = M * Vfrac_eq;
        const L_out = Math.max(0, M - V_out);
        const dH_outin_kJkg = ph_Hcalc - Hmix; // actual outlet - inlet (kJ/kg)
        const dH_req_kJkg = Htarget - Hmix; // implied by the energy balance (kJ/kg)
        lastReboilerDiag = {
          iter,
          T_K: Tnew,
          Vfrac: Vfrac_eq,
          Hin: Hmix,
          Htarget,
          Hout: ph_Hcalc,
          dH_outin_kJkg,
          dH_req_kJkg,
          dH_solver: ph_dH,
          V_out_kgps: V_out * feed_kgps,
          L_out_kgps: L_out * feed_kgps,
          status: phStatus,
          singlePhase: phSinglePhase,
        };
      }

      // Bottom-zone damping (Murphree-like) to reduce stiffness near the reboiler.
      // This is NOT a full Murphree implementation; it is a stabilizing relaxation on T (and indirectly V via flows).
      const isBottomZone = i <= 2; // trays 1–3
      const etaT_bot = 0.6;

      let T_eq_eff = Tnew;
      if (isBottomZone) {
        T_eq_eff = T[i] + etaT_bot * (Tnew - T[i]);
      }
      // relax T
      const Tlo = Math.min(Ttop, Tbottom),
        Thi = Math.max(Ttop, Tbottom);
      const Told = T[i];
      T[i] = clamp((1 - relaxT) * T[i] + relaxT * T_eq_eff, T_MIN, T_MAX);

      // With efficiency applied in EOSK/flashPH, the leaving compositions are simply the flash results.
      const y_out = y_eq;
      const x_eff = x_eq;

      const epsFlow = 1e-12;
      let V_out = Math.max(epsFlow * M, Vfrac_eq * M);
      let L_out = Math.max(epsFlow * M, (1 - Vfrac_eq) * M);

      // Apply side-draw (liquid)
      const fDraw = i === 0 ? 0 : clamp(Number(sideDrawSpecs[i] ?? 0), 0, 0.5);
      if (fDraw > 0 && L_out > 1e-10) {
        const L_draw_i = Math.min(fDraw * L_out, Math.max(0, L_out - 1e-12));
        L_out = Math.max(0, L_out - L_draw_i);
        sideDraw_dim[i] = L_draw_i;
      }

      V_up[i] = (1 - relax) * V_up[i] + relax * V_out;
      Y_up[i] = blendVec(Y_up[i], y_out, relax);
      next_L_dn[i] = L_out;
      next_X_dn[i] = x_eff;

      // Capture reboiler-adjacent tray flows for optional diagnostics (UI only).
      // Intentionally do NOT emit these to the Run log.
      if (i === 0 && iter % iterPrint === 0 && lastReboilerDiag) {
        lastReboilerDiag.V_out_kgps = V_out;
        lastReboilerDiag.L_out_kgps = L_out;
      }

      V_in = V_out;
      y_in = y_out;

      // update carry-over vapor enthalpy using the vapor leaving this tray at its (updated) tray temperature
      Hvap_carry = hVap(y_out, T[i], components, P[i]);

      if (trayPrint > 0 && i % trayPrint === 0) {
        tick({
          stage: "trayProgress",
          iter,
          tray: i,
          trays: N,
          dT: Math.abs(T[i] - Told),
          Vfrac_eq,
        });
      }
    }

    projectMonotoneTemps(T, f);

    // Enforce bottom-zone temperature monotonicity (physical constraint):
    // reboiler/bottom tray hottest, then temperatures decrease moving upward.
    // This prevents single-tray inversions (e.g., Tray 2 cold dip) that are non-physical in an ADT.
    const BOT_MONO_TRAYS = 4; // trays 1–4
    for (let j = 0; j < BOT_MONO_TRAYS - 1; j++) {
      if (T[j + 1] > T[j]) {
        const avg = 0.5 * (T[j] + T[j + 1]);
        T[j] = Math.max(T[j], avg);
        T[j + 1] = Math.min(T[j + 1], avg);
      }
    }

    smoothVectorMidpoint(V_up, 1, true);

    // --- Unit operations: total condenser + reboiler ---

    // Total condenser.
    // - temperature spec: Tc fixed, compute Qc from HB.
    // - duty spec:        Qc fixed, solve for Tc.
    // Always split condensed liquid into distillate + reflux using refluxRatio.
    const V_top = V_up[N - 2];
    const y_top = Y_up[N - 2];

    const D = V_top / Math.max(1e-6, 1 + refluxRatio);
    const Lref_new = refluxRatio * D;
    const xref_new = y_top.slice();

    // Condenser energy balance
    const mV_in_kgps = V_top * feed_kgps;
    const hV_in = hVap(
      y_top,
      clamp(T[N - 2], T_MIN, T_MAX),
      components,
      P[N - 2]
    );

    const cMode = String(condenserSpec || "temperature").toLowerCase();
    if (cMode === "duty") {
      // Hold Qc fixed and solve Tc.
      Qc_kW = Qc_set_kW;
      Tc = solveCondenserTcFromDuty({
        y: y_top,
        components,
        P: P[N - 1],
        mV_in_kgps,
        hV_in,
        Qc_target_kW: Qc_kW,
        Tseed: Tc,
        T_MIN,
        T_MAX,
      });
      if (Tc <= T_MIN + 1e-6) c_tcAtMin = true;
      if (Tc >= T_MAX - 1e-6) c_tcAtMax = true;
    } else {
      // Hold Tc fixed (Ttop input) and compute Qc.
      // NOTE: "reflux" spec is treated as an overhead split spec; the condenser
      // energy side defaults to temperature-fixed unless the user explicitly
      // selects Duty (Qc).
      Tc = Tc_set_K;
      const hL_out = hLiq(y_top, Tc, components, P[N - 1]);
      const Qc_HB_kW = -mV_in_kgps * (hV_in - hL_out);
      Qc_kW = clamp(Qc_HB_kW, -80000, 0);
    }

    // Reboiler.
    // - duty spec:        Qr fixed, Treb floats
    // - temperature spec: Treb fixed (Tbottom input), Qr floats
    // - boilup spec:      Vb/B fixed (reboilRatio input), Qr floats
    // Liquid from bottom tray (tray 1, index 0) flows to the reboiler; reboiler returns boil-up vapor and bottoms product.
    // Reboiler feed is the liquid leaving Tray 2 (index 1) downward into the reboiler drum.
    const L_to_reb = Math.max(1e-12, next_L_dn[1] ?? next_L_dn[0]);
    const x_to_reb = (next_X_dn[1] ?? next_X_dn[0] ?? feedZ).slice();

    const mL_in_kgps = L_to_reb * feed_kgps;
    const Hin_reb = hLiq(x_to_reb, clamp(T[1], T_MIN, T_MAX), components, P[0]);
    // Units:
    // - Hin_reb is in kJ/kg
    // - Qr_kW is kJ/s
    // - Dividing by mass flow (kg/s) yields kJ/kg
    // Therefore: do NOT divide by 1000 here.
    const Htarget_reb = Hin_reb + Qr_kW / Math.max(1e-12, mL_in_kgps);

    // OPTION 2 (recommended): saturated reboiler model.
    // Constrain the PH solve to the saturation envelope [Tbub, Tdew]
    // and clamp if the requested duty would otherwise require superheat.
    const reb = flashPH_saturated({
      P: P[0],
      z: x_to_reb,
      Htarget: Htarget_reb,
      components,
      eos: pickEOS(0),
      trayIndex: 0,
      Tseed: clamp(T[1], T_MIN, T_MAX),
      log: baseFlashOpts.log,
    });

    const Treb = clamp(reb.T, T_MIN, T_MAX);

    // Fix option 2 (more physical): treat Tray 1 as a *partial reboiler*.
    // A partial reboiler must produce *both* boil-up vapor and a liquid bottoms stream.
    // When the energy request would drive the saturated flash to V=1, HYSYS/UniSim
    // conceptually transitions to superheat; but our column needs a liquid bottoms.
    // So we cap V below 1.0 to prevent a "dry" reboiler (B=0).
    // Using 0.95 (instead of 0.995) ensures a clearly non-zero liquid bottoms stream
    // and avoids UI rounding showing V*=1.000.
    // Reboiler vapor fraction limiting
    // - Partial reboiler: keep a meaningful liquid bottoms stream (avoid a dry reboiler)
    // - Total reboiler: allow higher boil-up, but still prevent exactly-zero bottoms (numerical stability)
    const rbType = String(reboilerType || "partial").toLowerCase();
    const VFRAC_REB_MAX_USER = rbType === "total" ? 0.999 : 0.95;
    // Ensure at least a tiny bottoms fraction remains so B doesn't collapse to 0 from rounding.
    const MIN_B_DIM = 1e-4;
    const maxVByBmin = 1 - MIN_B_DIM / Math.max(1e-12, L_to_reb);
    const VFRAC_REB_MAX = Math.min(VFRAC_REB_MAX_USER, maxVByBmin);
    const Vfrac_raw = clamp(reb.V, 0, 1);
    const Vfrac_reb = Math.min(VFRAC_REB_MAX, Vfrac_raw);
    rb_vfracRaw = Vfrac_raw;
    rb_vfracUsed = Vfrac_reb;
    if (Vfrac_raw > VFRAC_REB_MAX + 1e-8) rb_vfracClamped = true;
    Vfrac_reb_last = Vfrac_reb;

    const Vboil_new = Math.max(1e-12, L_to_reb * Vfrac_reb);
    const B_new = Math.max(1e-12, L_to_reb * (1 - Vfrac_reb));
    const yboil_new = (reb.y ?? x_to_reb).slice();
    const xB_new = (reb.x ?? x_to_reb).slice();

    // Reboiler controller (updates Qr_kW for the NEXT iteration in temperature/boilup modes)
    const rbMode = String(reboilerSpec || "duty").toLowerCase();
    if (rbMode === "temperature") {
      const eT = Treb_set_K - Treb;
      ei_bot_int += eT;
      const Qr_proposed = Qr_kW + Kr_Q * eT + Ki_Q * ei_bot_int;
      Qr_kW = clamp(Qr_proposed, 0, 80000);
      if (Qr_kW <= 1e-9 && Qr_proposed < 0) rb_hitDutyMin = true;
      if (Qr_kW >= 80000 - 1e-9 && Qr_proposed > 80000) rb_hitDutyMax = true;
      if (iter % iterPrint === 0) {
        tick({ stage: "controller", iter, Qc_kW, Qr_kW, e_bot: eT, tol: 0 });
      }
    } else if (rbMode === "boilup") {
      const ratio = Vboil_new / Math.max(1e-12, B_new);
      const eR = (reboilRatio || 0) - ratio;
      ei_bot_int += eR;
      // Ratio is dimensionless; use stronger gains than kW/K.
      const K_ratio = 20000;
      const Ki_ratio = 2000;
      const Qr_proposed = Qr_kW + K_ratio * eR + Ki_ratio * ei_bot_int;
      Qr_kW = clamp(Qr_proposed, 0, 80000);
      if (Qr_kW <= 1e-9 && Qr_proposed < 0) rb_hitDutyMin = true;
      if (Qr_kW >= 80000 - 1e-9 && Qr_proposed > 80000) rb_hitDutyMax = true;
      if (iter % iterPrint === 0) {
        tick({ stage: "controller", iter, Qc_kW, Qr_kW, e_bot: eR, tol: 0 });
      }
    } else {
      // duty mode: keep Qr fixed
      Qr_kW = clamp(Qr_kW_in, 0, 80000);
    }

    // Reboiler drum temperature is Treb; couple Tray 2 (index 1) gently toward Treb to reduce stiffness.
    T[0] = Treb;
    if (N > 1) T[1] = clamp(0.85 * T[1] + 0.15 * Treb, T_MIN, T_MAX);

    // push downflow
    // L_dn[i] represents the liquid *leaving tray i* downward to tray i-1 (feed-anchored basis).
    // So we should relax it toward the newly computed next_L_dn[i] from this iteration.
    const L_dn_next = new Array(N).fill(0);
    const X_dn_next = new Array(N).fill(null);
    for (let i = 0; i < N - 1; i++) {
      L_dn_next[i] = (1 - relax) * (L_dn[i] || 0) + relax * next_L_dn[i];
      X_dn_next[i] = next_X_dn[i]
        ? blendVec(X_dn[i] || next_X_dn[i], next_X_dn[i], relax)
        : X_dn[i] || next_X_dn[i];
    }
    // Top tray liquid leaving downward is the reflux return (from condenser split)
    L_dn_next[N - 1] = Lref_new;
    X_dn_next[N - 1] = xref_new;
    L_dn = L_dn_next;
    X_dn = X_dn_next;

    // update boundary recycles (relaxed)
    L_ref = (1 - relax) * L_ref + relax * Lref_new;
    x_ref = blendVec(x_ref, xref_new, relax);
    V_boil = (1 - relax) * V_boil + relax * Vboil_new;
    y_boil = blendVec(y_boil, yboil_new, relax);

    // Store bottoms product (dimensionless) for final reporting.
    B_dim_last = B_new;
    xB_last = xB_new;
    Treb_last = Treb;

    // Basic convergence metric uses boundary recycle changes.
    const residSplit = Math.max(
      Math.abs(Lref_new - L_ref),
      Math.abs(Vboil_new - V_boil)
    );

    if (iter % iterPrint === 0) {
      tick({
        stage: "units",
        iter,
        Tc_K: Tc,
        Qc_kW,
        Treb_K: Treb,
        Qr_kW,
        Vboil_dim: Vboil_new,
        B_dim: B_new,
        resid: residSplit,
      });
    }

    // convergence
    const tolSplit = 1e-4;
    const tolTemp = 0.3;
    const splitOK =
      Math.abs(Lref_new - L_ref) < tolSplit &&
      Math.abs(Vboil_new - V_boil) < tolSplit;
    const tempOK =
      Math.max(Math.abs(T[N - 1] - Ttop_prev), Math.abs(T[0] - Tbot_prev)) <
      tolTemp;
    const Qstable = Math.abs(Qc_kW - last_Qc_kW) < 50;
    last_Qc_kW = Qc_kW;

    if (splitOK && tempOK && iter > 10) {
      tick({ stage: "converged", iter, resid: residSplit });
      break;
    }

    // Fallback: if splits and duties are stable for a long time AND temperatures are nearly within tolerance,
    // allow exit to avoid excessive runtime.
    const tempNearlyOK =
      Math.max(Math.abs(T[N - 1] - Ttop_prev), Math.abs(T[0] - Tbot_prev)) <
      2 * tolTemp;
    if (splitOK && Qstable && tempNearlyOK && iter > 200) {
      tick({ stage: "near-converged", iter, resid: residSplit });
      break;
    }
  }

  // ---------- Dimensionless products ----------
  const V_top_final = V_up[N - 2];
  const L_to_reb_final = L_dn[0];

  const D_dim = V_top_final / Math.max(1e-6, 1 + refluxRatio);
  const B_dim = Math.max(0, B_dim_last);

  const totalSide_dim = sideDraw_dim.reduce((a, b) => a + b, 0);
  const totalProducts_dim = D_dim + totalSide_dim + B_dim;

  // === Two independent scales ===
  const mScale_products =
    totalProducts_dim > 1e-12 ? feedRate_kgph / totalProducts_dim : 0; // ensures Σproducts = feed
  const mScale_internal = feedRate_kgph; // keeps internal traffic realistic at feed-tray

  // Products on feed basis
  const D_kgph = D_dim * mScale_products;
  const B_kgph = B_dim * mScale_products;
  const sideDraws_kgph = sideDraw_dim.map((v) => v * mScale_products);

  // Internal boundary recycles (display only)
  const L_ref_kgph = L_ref * mScale_internal;
  const V_boil_kgph = V_boil * mScale_internal;

  // ---------- Final reporting ----------
  const traysOut = new Array(N);
  for (let i = 0; i < N; i++) {
    // Boundary drums (unit ops)
    if (i === 0) {
      // Reboiler drum
      // Expose the partial-reboiler identity on a *product basis* so the UI can display it:
      //   B = (1 - V_frac) * F_reb
      // where F_reb is the (liquid) feed entering the reboiler.
      const Vreb = clamp(Vfrac_reb_last, 0, 1);
      const Freb_kgph = B_kgph / Math.max(1e-12, 1 - Vreb);
      traysOut[i] = {
        i: 1,
        T: clamp(Treb_last, T_MIN, T_MAX),
        T_internal: clamp(Treb_last, T_MIN, T_MAX),
        P: P[i],
        V: Vreb,
        x: xB_last.slice(),
        y: y_boil.slice(),
        m_vap_up_kgph: V_boil * mScale_internal,
        // For the reboiler "tray", the relevant liquid stream is the *bottoms product*.
        // Showing this here avoids the confusing "L↓=0" even when a bottoms draw exists.
        m_liq_dn_kgph: B_kgph,
        // Product-basis reboiler feed implied by the partial-reboiler split identity.
        // (Used for display only.)
        reboilerFeed_kgph: Freb_kgph,
        bottomsFromSplit_kgph: (1 - Vreb) * Freb_kgph,
        sideDraw_kgph: 0.0,
        sideDraw_frac: 0.0,
      };
      continue;
    }
    if (i === N - 1) {
      // Condenser drum (all liquid at Tc); vapor fraction shown as 0
      traysOut[i] = {
        i: N,
        T: Tc,
        T_internal: Tc,
        P: P[i],
        V: 0.0,
        x: x_ref.slice(),
        y: (Y_up[i - 1] ?? feedZ).slice(),
        m_vap_up_kgph: 0.0,
        m_liq_dn_kgph: L_ref * mScale_internal,
        sideDraw_kgph: 0.0,
        sideDraw_frac: 0.0,
      };
      continue;
    }
    const L_in = i === N - 1 ? L_ref : L_dn[i + 1] ?? L_ref;
    const x_in = i === N - 1 ? x_ref : X_dn[i + 1] ?? x_ref;
    const V_in = i === 0 ? V_boil : V_up[i - 1];
    const y_in = i === 0 ? y_boil : Y_up[i - 1];
    const addV = i === f ? Vf : 0;
    const addL = i === f ? 1 - Vf : 0;

    const M = Math.max(1e-12, V_in + L_in + addV + addL);
    const z = new Array(feedZ.length).fill(0);
    for (let k = 0; k < z.length; k++) {
      const num = V_in * y_in[k] + L_in * x_in[k] + addV * yf[k] + addL * xf[k];
      z[k] = Math.max(0, num / M);
    }
    normalize(z);

    const Tvin = i > 0 ? T[i - 1] : T[i];
    const Tlin = i < N - 1 ? T[i + 1] : T[i];
    const T_liq_in = i === N - 1 ? Tc : Tlin;
    const Hmix =
      (V_in * hVap(y_in, Tvin, components, P[i]) +
        L_in * hLiq(x_in, T_liq_in, components, P[i]) +
        addV * hVap(yf, T[i], components, P[i]) +
        addL * hLiq(xf, T[i], components, P[i])) /
      Math.max(1e-12, M);

    // The production flash for final properties (trays are adiabatic; condenser/reboiler are separate units)
    const Htarget = Hmix;

    const etaV = etaBySection(
      i,
      N,
      f,
      murphree.etaV_top,
      murphree.etaV_mid,
      murphree.etaV_bot
    );

    const {
      T: Tfin_withQ,
      V: Vfrac_eq,
      x: x_eq,
      y: y_eq,
    } = flashPH({
      P: P[i],
      z,
      Htarget,
      components,
      trayIndex: i,
      trays: N,
      Tseed: T[i],
      murphreeEtaV: etaV,
    });

    // Efficiency is handled inside EOSK/flashPH (effective K-values), so the flash compositions are the effective leaves.
    const y_eff = y_eq;
    const x_eff = x_eq;

    // small reporting relax on the **internal** T (keep with-Q for internal state)
    // Display the *tray* temperature used by the solver (includes utility duty at boundaries).
    const T_internal = clamp(Tfin_withQ, T_MIN, T_MAX);

    // Display the *tray* temperature (no utility duty applied) at the boundaries to avoid
    // artificial spikes caused by mixing tray + utility temperatures in the profile.
    const Tdisplay = T_internal;

    // INTERNAL traffic shown on feed basis
    const m_vap_up_kgph = V_up[i] * mScale_internal;
    const m_liq_dn_kgph = L_dn[i] * mScale_internal;

    traysOut[i] = {
      i: i + 1,
      T: Tdisplay,
      T_internal: T_internal,
      P: P[i],
      V: clamp(Vfrac_eq, 0, 1),
      x: x_eff,
      y: y_eff,
      m_vap_up_kgph,
      m_liq_dn_kgph,
      sideDraw_kgph: sideDraws_kgph[i] || 0,
      sideDraw_frac: clamp(Number(sideDrawSpecs[i] ?? 0), 0, 0.5),
    };
  }

  const Vs = traysOut.map((t) => t.V);
  smoothVectorMidpoint(Vs, 1, true);
  for (let i = 0; i < N; i++) traysOut[i].V = clamp(Vs[i], 0, 1);

  const reflux_fraction = clamp(
    L_ref / Math.max(1e-6, L_ref + V_up[N - 1]) || 0,
    0,
    1
  );
  const boilup_fraction = clamp(
    V_boil / Math.max(1e-6, V_boil + B_dim_last) || 0,
    0,
    1
  );

  // ---------- UI diagnostics (single-line banner uses the highest-severity item) ----------
  // Always provide a concise "what is being solved" message.
  {
    const cMode = String(condenserSpec || "temperature").toLowerCase();
    const rbMode = String(reboilerSpec || "duty").toLowerCase();

    const cText =
      cMode === "duty"
        ? "Condenser spec: Duty (Tc calculated)"
        : "Condenser spec: Temperature (Qc calculated)";
    const rText =
      rbMode === "temperature"
        ? "Reboiler spec: Temperature (Qr calculated)"
        : rbMode === "boilup"
        ? "Reboiler spec: Boilup ratio (Qr calculated)"
        : "Reboiler spec: Duty (Treb calculated)";
    addDiag("info", "ACTIVE_SPECS", `${cText} | ${rText}`);

    if (rb_vfracClamped) {
      addDiag(
        "warning",
        "REB_VFRAC_CLAMP",
        `Reboiler vapor fraction limited to keep a liquid bottoms draw (V used=${(
          rb_vfracUsed ?? 0
        ).toFixed(3)}; raw=${(rb_vfracRaw ?? 0).toFixed(3)}).`
      );
    }
    if (rb_hitDutyMax) {
      addDiag(
        "warning",
        "REB_Q_MAX",
        "Reboiler duty hit the upper limit (Qr clamped), so the selected spec may not be fully achievable."
      );
    }
    if (rb_hitDutyMin) {
      addDiag(
        "warning",
        "REB_Q_MIN",
        "Reboiler duty hit the lower limit (Qr clamped), so the selected spec may not be fully achievable."
      );
    }
    if (c_tcAtMin || c_tcAtMax) {
      addDiag(
        "warning",
        "COND_T_LIMIT",
        "Condenser temperature reached an internal safety limit while solving the duty spec."
      );
    }
  }

  tick({ stage: "final", status: "OK" });

  const T_overhead_cold_K = Tc;
  const T_reboiler_hot_K = Treb_last;

  // Mass balance summary (products only)
  const massBalance = {
    feed_kgph: feedRate_kgph,
    overhead_kgph: D_kgph,
    sideDraws_kgph,
    bottoms_kgph: B_kgph,
    totalProducts_kgph:
      D_kgph + B_kgph + sideDraws_kgph.reduce((a, b) => a + b, 0),
  };

  return {
    trays: traysOut,
    feedTray,
    draws:
      drawLabels && Object.keys(drawLabels).length
        ? drawLabels
        : defaultDrawMap(),
    status: "OK",
    diagnostics,
    energy: {
      Qc_kW,
      Qr_kW,
      reflux_fraction,
      boilup_fraction,
      feedRate_kgph,

      // Boundary specification bookkeeping for the UI
      condenserSpec: String(condenserSpec || "temperature"),
      reboilerSpec: String(reboilerSpec || "duty"),
      condenserType: String(condenserType || "total"),
      reboilerType: String(reboilerType || "partial"),
      specs: {
        condenser: {
          Tc_set_K,
          Qc_set_kW,
          Tc_calc_K: T_overhead_cold_K,
          Qc_calc_kW: Qc_kW,
          refluxRatio_set: refluxRatio,
          condenserType: String(condenserType || "total"),
          // IMPORTANT: compute ratios on a consistent basis.
          // L_ref_kgph uses the *internal traffic* scale, while D_kgph uses the
          // *product* scale. Dividing those can produce wildly wrong values.
          // Instead, compute the dimensionless reflux ratio using dimensionless
          // internal flows.
          refluxRatio_calc: L_ref / Math.max(1e-6, D_dim) || 0,
        },
        reboiler: {
          Treb_set_K,
          reboilerType: String(reboilerType || "partial"),
          Qr_set_kW: clamp(Qr_kW_in, 0, 80000),
          Treb_calc_K: T_reboiler_hot_K,
          Qr_calc_kW: Qr_kW,
          boilupRatio_set: reboilRatio,
          // Same basis issue as refluxRatio_calc; compute on dimensionless flows.
          boilupRatio_calc: V_boil / Math.max(1e-6, B_dim) || 0,
        },
      },

      // expose both scales so the UI can choose how to render
      mScale_internal: mScale_internal,
      mScale_products: mScale_products,

      D_kgph,
      B_kgph,
      L_ref_kgph,
      V_boil_kgph,
      sideDraws_kgph,
      massBalance,
    },
    boundary: {
      condenser: { T_cold_K: T_overhead_cold_K },
      reboiler: { T_hot_K: T_reboiler_hot_K },
    },
  };
}

/* ================= helpers ================= */
function etaBySection(i, N, f, etaTop, etaMid, etaBot) {
  if (i <= Math.floor(f / 2)) return clamp(etaBot ?? 1, 0, 1);
  if (i >= Math.ceil((f + N - 1) / 2)) return clamp(etaTop ?? 1, 0, 1);
  return clamp(etaMid ?? 1, 0, 1);
}

// Solve condenser outlet temperature Tc for a specified duty Qc.
// Uses a simple bracketed bisection on:
//   Q(T) = -mV*(hV_in - hL_out(T))
// Notes:
// - Qc is negative for heat removed.
// - If no bracket is found, falls back to the seed temperature.
function solveCondenserTcFromDuty({
  y,
  components,
  P,
  mV_in_kgps,
  hV_in,
  Qc_target_kW,
  Tseed,
  T_MIN,
  T_MAX,
}) {
  const m = Math.max(0, mV_in_kgps || 0);
  if (!Number.isFinite(m) || m < 1e-12) {
    return clamp(Tseed, T_MIN, T_MAX);
  }

  const Qtgt = clamp(Qc_target_kW, -80000, 0);
  const fQ = (T) => {
    const hL = hLiq(y, T, components, P);
    const Q = -m * (hV_in - hL);
    return Q - Qtgt;
  };

  let lo = clamp(T_MIN, T_MIN, T_MAX);
  let hi = clamp(Math.max(Tseed ?? lo + 1, lo + 1), T_MIN, T_MAX);
  let flo = fQ(lo);
  let fhi = fQ(hi);

  // Try to find a bracket by expanding hi.
  if (!(flo === 0 || fhi === 0) && flo * fhi > 0) {
    hi = T_MAX;
    fhi = fQ(hi);
  }

  if (!(flo === 0 || fhi === 0) && flo * fhi > 0) {
    // No bracket: fall back to seed.
    return clamp(Tseed, T_MIN, T_MAX);
  }

  // Bisection
  let a = lo,
    b = hi,
    fa = flo,
    fb = fhi;
  for (let k = 0; k < 35; k++) {
    const mid = 0.5 * (a + b);
    const fm = fQ(mid);
    if (!Number.isFinite(fm)) break;
    if (Math.abs(fm) < 1e-4) return clamp(mid, T_MIN, T_MAX);
    if (fa * fm <= 0) {
      b = mid;
      fb = fm;
    } else {
      a = mid;
      fa = fm;
    }
  }
  return clamp(0.5 * (a + b), T_MIN, T_MAX);
}

function murphreeBlend(inlet, eq, eta) {
  const out = new Array(eq.length);
  const w = clamp(eta ?? 1, 0, 1);
  for (let k = 0; k < eq.length; k++) out[k] = (1 - w) * inlet[k] + w * eq[k];
  normalize(out);
  return out;
}

function initTrayTempsTwoSegment({ N, Ttop, Tbottom, Tfeed, f }) {
  const T = new Array(N).fill(0);
  for (let i = f; i < N; i++) {
    const a = (i - f) / Math.max(1, N - 1 - f);
    T[i] = (1 - a) * Tfeed + a * Ttop;
  }
  for (let i = 0; i <= f; i++) {
    const a = (f - i) / Math.max(1, f);
    T[i] = (1 - a) * Tfeed + a * Tbottom;
  }
  return T;
}

function initTrayPressures({ N, Ptop, Pdrop }) {
  const P = new Array(N).fill(0);
  for (let i = 0; i < N; i++) P[i] = Ptop + i * Pdrop;
  return P;
}

function normalize(v) {
  const s = v.reduce((a, b) => a + b, 0) || 1;
  for (let i = 0; i < v.length; i++) v[i] = Math.max(0, v[i] / s);
}

function blendVec(a, b, w) {
  if (!a) return b.slice();
  const o = new Array(a.length);
  for (let i = 0; i < o.length; i++) o[i] = (1 - w) * a[i] + w * b[i];
  normalize(o);
  return o;
}

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

function projectMonotoneTemps(T, f) {
  for (let i = f; i < T.length - 1; i++)
    if (T[i + 1] > T[i]) T[i + 1] = 0.5 * (T[i + 1] + T[i]);
  for (let i = f; i > 0; i--)
    if (T[i - 1] < T[i]) T[i - 1] = 0.5 * (T[i - 1] + T[i]);
}

function smoothVectorMidpoint(v, passes = 1, keepEnds = true) {
  for (let p = 0; p < passes; p++) {
    const prev = v.slice();
    for (let i = 0; i < v.length; i++) {
      if (keepEnds && (i === 0 || i === v.length - 1)) continue;
      const left = prev[i - 1] ?? prev[i];
      const right = prev[i + 1] ?? prev[i];
      v[i] = 0.5 * prev[i] + 0.25 * (left + right);
    }
  }
}

function defaultDrawMap() {
  // Labels for typical ADT cuts (UI only)
  return {
    32: "C1–C4 Overhead",
    30: "Light Naphtha",
    26: "Heavy Naphtha",
    22: "Kerosene",
    16: "LGO",
    10: "HGO",
    1: "Residue",
  };
}
